import React, {useCallback, useEffect, useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {Form, Spinner} from 'react-bootstrap';
import {Content, Popover, Trigger} from '@cabinetsbycomputer/popover';
import {Icon} from 'shared/helpers';
import {useMouseOver} from 'shared/helpers/useMouseOver';
import {
    Number as WidthInput,
    NumberSpinner,
} from 'components/customer/Product/QuickAdd/NumberSpinner';
import {lightenColor} from 'components/customer/Confirmation/helpers/lightenColor';
import {
    clampPlaceholderQuantity,
    MAX_PLACEHOLDER_QUANTITY,
} from 'components/customer/RoomPlanner/helpers/placeholder/quantity';

interface PlaceholderQuickAddProps {
    faceId: number;
    loading: boolean;
    disabled?: boolean;
    /** Admin locked the size: Quick Add only, no Customise. */
    canCustomise?: boolean;
    errorMessage?: string;
    onClearError?: () => void;
    /** Bumps when the error toast is clicked, so the details popover reopens. */
    revealError?: number;
    /** Catalog width in mm, shown in the same field as a product quick add. */
    defaultWidth?: number;
    onAdd: (quantity: number, width: number) => Promise<void>;
    onClickHandler: () => void;
}

/**
 * Same card actions as a cabinet product: Customise, then Quick Add.
 * Quick Add always shows width. When the admin has locked the size the
 * field stays disabled on the catalog width, and Customise is omitted.
 * @return {JSX.Element}
 */
export const PlaceholderQuickAdd = ({
    faceId,
    loading,
    disabled = false,
    canCustomise = true,
    errorMessage = '',
    onClearError,
    revealError = 0,
    defaultWidth = 0,
    onAdd,
    onClickHandler,
}: PlaceholderQuickAddProps) => {
    const [quantity, setQuantity] = useState(1);
    const [width, setWidth] = useState(defaultWidth);
    const [open, setOpen] = useState(false);
    const {isMouseOverComponent, handleMouseEnter, handleMouseExit} =
        useMouseOver();

    useEffect(() => {
        setWidth(defaultWidth);
    }, [defaultWidth]);

    useEffect(() => {
        if (errorMessage) setOpen(true);
    }, [errorMessage, revealError]);

    const setClampedQuantity = useCallback(
        (next: React.SetStateAction<number>) => {
            onClearError?.();
            setQuantity((current) => {
                const value = typeof next === 'function' ? next(current) : next;
                return clampPlaceholderQuantity(
                    Math.min(value, MAX_PLACEHOLDER_QUANTITY)
                );
            });
        },
        [onClearError]
    );

    const handleOpenChange = useCallback((next: boolean) => {
        setOpen(next);
    }, []);

    const handleWidthChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const next = parseFloat(event.target.value);
            setWidth(Number.isNaN(next) ? 0 : next);
            onClearError?.();
        },
        [onClearError]
    );

    const handleCancel = useCallback(() => {
        setOpen(false);
        setQuantity(1);
        setWidth(defaultWidth);
        onClearError?.();
    }, [defaultWidth, onClearError]);

    const handleAdd = useCallback(() => {
        if (loading || disabled || errorMessage) return;
        // A locked size cannot be typed, so Add keeps the catalog width.
        const widthToAdd = canCustomise ? width : defaultWidth;
        void onAdd(quantity, widthToAdd).then(
            () => {
                setOpen(false);
                setQuantity(1);
                setWidth(defaultWidth);
            },
            () => {
                setOpen(true);
            }
        );
    }, [
        loading,
        disabled,
        errorMessage,
        onAdd,
        quantity,
        width,
        defaultWidth,
        canCustomise,
    ]);

    const handleQuickAddClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (!open) return;
            event.preventDefault();
            handleAdd();
        },
        [open, handleAdd]
    );

    return (
        <Actions>
            {canCustomise ? (
                <CustomizeButton
                    type="button"
                    onClick={onClickHandler}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseExit}>
                    <Icon
                        color={isMouseOverComponent ? 'secondary' : 'primary'}
                        iconName="Options-Edit-Primary.svg"
                    />
                    <label>Customise</label>
                </CustomizeButton>
            ) : null}
            <Popover
                open={open || Boolean(errorMessage)}
                onOpenChange={handleOpenChange}>
                <QuickAddButton
                    type="button"
                    $hasError={Boolean(errorMessage)}
                    disabled={disabled}
                    onClick={handleQuickAddClick}>
                    {loading ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <Icon iconName="Header-Cart.svg" />
                    )}
                    <label>Quick Add</label>
                </QuickAddButton>
                <PopoverContent
                    container={
                        document.querySelector('#products-container') ??
                        document.body
                    }
                    zIndex={11}
                    sideOffset={12}
                    collisionPadding={20}>
                    <InputControl $hasError={Boolean(errorMessage)}>
                        <div>
                            <label htmlFor={`placeholder-width-${faceId}`}>
                                Width (mm)
                            </label>
                            <Form.Group>
                                <WidthField
                                    id={`placeholder-width-${faceId}`}
                                    value={width || ''}
                                    onChange={handleWidthChange}
                                    type="number"
                                    isInvalid={Boolean(errorMessage)}
                                    disabled={!canCustomise}
                                />
                            </Form.Group>
                        </div>
                        <div>
                            <label htmlFor={`placeholder-quantity-${faceId}`}>
                                Qty
                            </label>
                            <NumberSpinner
                                quantity={quantity}
                                setQuantity={setClampedQuantity}
                            />
                        </div>
                    </InputControl>
                    {errorMessage ? (
                        <Error>
                            <Icon iconName="Error.svg" />
                            <span>{errorMessage}</span>
                        </Error>
                    ) : null}
                    <ButtonContainer>
                        <CancelButton
                            type="button"
                            disabled={loading}
                            onClick={handleCancel}>
                            Cancel
                        </CancelButton>
                        <AddButton
                            type="button"
                            data-cy="placeholder-quick-add-button"
                            disabled={
                                loading || disabled || Boolean(errorMessage)
                            }
                            onClick={handleAdd}>
                            {loading ? (
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">
                                        Loading...
                                    </span>
                                </Spinner>
                            ) : (
                                <Icon iconName="Header-Cart.svg" />
                            )}
                            <label>Add</label>
                        </AddButton>
                    </ButtonContainer>
                </PopoverContent>
            </Popover>
        </Actions>
    );
};

const buttonStyle = css`
    border-radius: 16px;
    font-size: 0.8em;
    font-weight: 500;
    padding: 2px 2px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    > label {
        cursor: pointer;
        flex: 1;
    }

    > img {
        width: 22px;
        margin-right: 6px;
    }
`;

const Actions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const CustomizeButton = styled.button`
    ${buttonStyle}
    background: white;
    border: 2px solid ${({theme}) => theme.colors.primary?.main};
    color: ${({theme}) => theme.colors.primary?.main};

    &:hover,
    &:focus,
    &:active {
        border-color: ${({theme}) => theme.colors.secondary?.main};
        color: ${({theme}) => theme.colors.secondary?.main};
    }

    transition:
        color 500ms ease-in-out,
        background 500ms ease-in-out,
        border-color 500ms ease-in-out;
`;

const QuickAddButton = styled(Trigger)<{$hasError?: boolean}>`
    ${buttonStyle}
    background: ${({theme, $hasError}) =>
        $hasError ? '#dc3545' : theme.colors.secondary?.main};
    border: 0;
    padding: 4px;
    color: white;
    transition:
        background 500ms ease-in-out,
        border-color 500ms ease-in-out;

    &:hover,
    &:focus,
    &:active {
        background: ${({theme, $hasError}) =>
            $hasError ? '#dc3545' : theme.colors.primary?.main};
    }

    &:disabled {
        opacity: 0.6;
    }

    > div {
        width: 22px;
        height: 22px;
        border-width: 3px;
    }
`;

const contentEnter = keyframes`
    from {
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`;

const contentExit = keyframes`
    from {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
    to {
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
    }
`;

const PopoverContent = styled(Content)`
    background: ${({theme}) => lightenColor(theme.colors.secondary?.main, 85)};
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 15px 15px;
    box-sizing: border-box;
    max-width: calc(var(--radix-popper-anchor-width) + 41px);
    border: 1px solid ${({theme}) => theme.colors.secondary?.main};
    border-radius: 10px;
    box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.16),
        0 2px 6px rgba(0, 0, 0, 0.08);

    &[data-state='open'] {
        animation: ${contentEnter} 220ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    &[data-state='closed'] {
        animation: ${contentExit} 140ms ease-in forwards;
    }
`;

const InputControl = styled.div<{$hasError?: boolean}>`
    display: flex;
    flex-direction: row;
    gap: 10px;

    > div:first-child {
        flex-basis: 80%;
    }

    div > label {
        font-size: 0.75em;
        font-weight: 500;
        color: ${({theme, $hasError}) =>
            $hasError ? '#dc3545' : theme.colors.secondary?.main};
        text-transform: uppercase;
        margin-bottom: 3px;
    }
`;

const WidthField = styled(WidthInput)`
    &:disabled {
        background: #f3f3f3;
        color: #6b6f70;
        cursor: not-allowed;
        opacity: 0.75;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const actionButton = css`
    border-radius: 20px;
    font-weight: 400;
    font-size: 0.85em;
    flex: 1;
    padding: 0 2px;
`;

const CancelButton = styled.button`
    ${actionButton}
    border: 2px solid #dfe3e8;
    background: white;
    color: #434343;

    &:hover,
    &:focus,
    &:active {
        border-color: ${({theme}) => theme.colors.primary?.main};
        color: ${({theme}) => theme.colors.primary?.main};
    }
`;

const Error = styled.div`
    background: #dc3545;
    font-size: 0.8em;
    line-height: 1.2;
    padding: 10px;
    color: white;
    border-radius: 10px;
    font-weight: 400;
    display: flex;
    gap: 10px;
    min-width: 0;

    > img {
        width: 25px;
        flex: 0 0 25px;
    }

    > span {
        min-width: 0;
        overflow-wrap: anywhere;
    }
`;

const AddButton = styled.button`
    ${actionButton}
    padding: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 0;
    background: ${({theme}) => theme.colors.secondary?.main};
    color: white;

    &:hover,
    &:focus,
    &:active {
        background: ${({theme}) => theme.colors.primary?.main};
    }

    &:disabled {
        opacity: 0.6;
    }

    > label {
        flex: 1;
        cursor: pointer;
    }

    > img {
        width: 20px;
    }

    > div {
        width: 20px;
        height: 20px;
        border-width: 3px;
    }
`;
